import type { PillTone } from '@bcecloud/portal-ui';
import { getJson } from '@/lib/api';

/**
 * Analytics → Page tests: a typed fetcher over /api/v3/analytics/ab-tests.
 * Shapes mirror apps/api's AbTestsResponse (api/v3/ab-tests/ab-test-stats.ts);
 * the test registry itself lives in the API (api/v3/ab-tests/page-tests.ts)
 * and arrives with each report.
 */

export const PAGE_TEST_VERSION = { a: 'a', b: 'b' } as const;
export type PageTestVersion = (typeof PAGE_TEST_VERSION)[keyof typeof PAGE_TEST_VERSION];
export const PAGE_TEST_VERSIONS: readonly PageTestVersion[] = [PAGE_TEST_VERSION.a, PAGE_TEST_VERSION.b];

export const AB_TEST_STATUS = {
  collecting: 'collecting',
  earlyHarm: 'early_harm',
  readyToRead: 'ready_to_read',
  srmWarning: 'srm_warning',
} as const;
export type AbTestStatus = (typeof AB_TEST_STATUS)[keyof typeof AB_TEST_STATUS];

export const AB_TEST_VERDICT = {
  bWins: 'b_wins',
  aWins: 'a_wins',
  noClearDifference: 'no_clear_difference',
  bFailsGuardrail: 'b_fails_guardrail',
} as const;
export type AbTestVerdict = (typeof AB_TEST_VERDICT)[keyof typeof AB_TEST_VERDICT];

export interface PageTestDefinition {
  readonly key: string;
  readonly short: string;
  readonly name: string;
  readonly path: string;
  readonly ga4Param: string;
  readonly startedAt: string;
  readonly plannedReadAt: string;
  readonly endedAt: string | null;
  readonly baselinePaidCvr: number;
  readonly hypothesis: string;
  readonly specUrl: string;
}

export interface AbTestVersionMetrics {
  readonly version: PageTestVersion;
  readonly sessions: number;
  readonly paidSessions: number;
  readonly leads: number;
  readonly paidLeads: number;
  readonly qualifiedPaidLeads: number;
  readonly proposals: number;
  readonly signed: number;
  readonly paidCvr: number | null;
  readonly qualifiedPerPaidSession: number | null;
  readonly qualifiedShare: number | null;
}

export interface AbTestReport {
  readonly test: PageTestDefinition;
  readonly status: AbTestStatus;
  /** Null until the test is ready to read. */
  readonly verdict: AbTestVerdict | null;
  readonly statusReason: string;
  readonly versions: Readonly<Record<PageTestVersion, AbTestVersionMetrics>>;
  readonly comparison: {
    readonly qualifiedPerPaidSessionLift: number | null;
    readonly paidCvrLift: number | null;
    readonly probabilityBBetter: number | null;
    readonly qualifiedShareChange: number | null;
  };
  readonly srm: { readonly chiSquare: number; readonly pValue: number; readonly flagged: boolean } | null;
  readonly progress: {
    readonly daysRun: number;
    readonly paidSessions: number;
    readonly plannedPaidSessions: number;
    readonly share: number;
  };
  readonly series: readonly {
    readonly date: string;
    readonly versions: Readonly<
      Record<
        PageTestVersion,
        { readonly paidSessions: number; readonly qualifiedPaidLeads: number; readonly cumulativeQualifiedPerPaidSession: number | null }
      >
    >;
  }[];
  readonly leads: readonly AbTestLeadRow[];
  readonly unmatchedSubmissions: number;
  readonly sessionsThrough: string | null;
}

export interface AbTestLeadRow {
  readonly salesforceId: string;
  readonly submittedAt: string;
  readonly company: string;
  readonly version: PageTestVersion;
  readonly status: string;
  readonly paid: boolean;
  readonly qualified: boolean;
  readonly proposal: boolean;
  readonly signed: boolean;
}

export interface AbTestsResponse {
  readonly generatedAt: string;
  readonly tests: readonly AbTestReport[];
}

export function readPageTests(signal?: AbortSignal): Promise<AbTestsResponse> {
  return getJson<AbTestsResponse>('/analytics/ab-tests', {}, signal);
}

/** The status pill: words in the reader's voice, and a tone. */
export const STATUS_PILL: Readonly<Record<AbTestStatus, { readonly label: string; readonly tone: PillTone }>> = {
  collecting: { label: 'Collecting data', tone: 'neutral' },
  early_harm: { label: 'Possible harm', tone: 'danger' },
  ready_to_read: { label: 'Ready to read', tone: 'success' },
  srm_warning: { label: 'Split looks off', tone: 'warning' },
};

export const VERDICT_LABELS: Readonly<Record<AbTestVerdict, string>> = {
  b_wins: 'Version B wins on qualified leads per paid session.',
  a_wins: 'Version A (the current page) wins. Keep it.',
  no_clear_difference: 'No clear difference. Keep version A.',
  b_fails_guardrail: 'Version B brings more qualified leads per paid session, but its qualified share fell more than 10 points. Do not ship it as is.',
};

export const VERSION_LABELS: Readonly<Record<PageTestVersion, string>> = { a: 'A (current)', b: 'B (new)' };
